import React, { useState } from 'react';
import { ArrowRight, Check, Loader2, ShieldCheck } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import { getAttribution, track } from '../lib/analytics';

const empty = { name: '', email: '', phone: '', company: '', product: 'both', message: '' };
const inputCls = 'mt-1 h-10 rounded-lg border-[var(--line-2)] bg-white';

// Compact lead form for the homepage hero — same backend + anti-spam as the contact page.
export const HeroLeadForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(empty);
  const [website, setWebsite] = useState('');
  const [startedAt] = useState(() => Date.now());
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const change = (f) => (e) => setForm({ ...form, [f]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', { ...form, source: 'home_hero', website, form_started_at: startedAt, attribution: getAttribution() });
      track('demo_request_submit', { label: form.product, product: form.product, cta_location: 'home_hero' });
      setDone(true);
    } catch (err) {
      toast({ title: 'Something went wrong', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl border border-[var(--line-2)] shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)] p-8 text-center" data-testid="hero-form-success">
        <div className="w-12 h-12 mx-auto rounded-full bg-[var(--badge-bg)] flex items-center justify-center mb-4">
          <Check className="w-5 h-5 text-[#4F46E5]" strokeWidth={2.5} />
        </div>
        <h3 className="text-xl font-semibold text-ink tracking-tight">Thanks, {form.name.split(' ')[0] || 'there'}.</h3>
        <p className="text-sm text-ink-2 mt-2">Our team will reach out within one business day to schedule your walkthrough.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="hero-lead-form"
      className="relative bg-white rounded-2xl border border-[var(--line-2)] shadow-[0_24px_60px_-20px_rgba(15,23,42,0.18)] p-6 lg:p-7 space-y-3"
    >
      <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden opacity-0 pointer-events-none" aria-hidden="true">
        <label htmlFor="hero-website">Website</label>
        <input id="hero-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>

      <div className="mb-2">
        <div className="eyebrow-pill">Request a demo</div>
        <h3 className="text-xl font-semibold text-ink tracking-tight mt-3">See BriefingIQ in action.</h3>
        <p className="text-xs text-ink-3 mt-1">A focused 30-minute demo, tailored to your briefing workflow.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="hero-name" className="text-[10px] uppercase tracking-wider text-ink-3">Full name</Label>
          <Input id="hero-name" required value={form.name} onChange={change('name')} className={inputCls} placeholder="Jane Doe" data-testid="hero-name-input" />
        </div>
        <div>
          <Label htmlFor="hero-company" className="text-[10px] uppercase tracking-wider text-ink-3">Company</Label>
          <Input id="hero-company" required value={form.company} onChange={change('company')} className={inputCls} placeholder="Acme Corp" data-testid="hero-company-input" />
        </div>
      </div>
      <div>
        <Label htmlFor="hero-email" className="text-[10px] uppercase tracking-wider text-ink-3">Work email</Label>
        <Input id="hero-email" type="email" required value={form.email} onChange={change('email')} className={inputCls} placeholder="jane@company.com" data-testid="hero-email-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="hero-phone" className="text-[10px] uppercase tracking-wider text-ink-3">Phone (optional)</Label>
          <Input id="hero-phone" type="tel" autoComplete="tel" value={form.phone} onChange={change('phone')} className={inputCls} placeholder="+1 (555) 000-0000" data-testid="hero-phone-input" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-3">I&apos;m interested in</Label>
          <Select value={form.product} onValueChange={change('product')}>
            <SelectTrigger className={inputCls} data-testid="hero-product-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="briefingiq">BriefingIQ — Briefings</SelectItem>
              <SelectItem value="eventiq">EventIQ — Events</SelectItem>
              <SelectItem value="both">Both products</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60 !mt-4" data-testid="hero-submit-btn">
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request demo <ArrowRight className="w-4 h-4" /></>}
      </button>
      <div className="flex items-center justify-center gap-1.5 text-[10px] text-ink-3">
        <ShieldCheck className="w-3 h-3" /> Your information stays private and is only used to arrange your demo.
      </div>
    </form>
  );
};

export default HeroLeadForm;
